// src/services/authService.js
// ======================================================
// 🔐 AUTH SERVICE - NIVEL ENTERPRISE (AIRBNB/QUINTOANDAR)
// ======================================================
// ✅ Autenticação UNIFICADA (Firebase + DEMO)
// ✅ Detecção automática de modo (PRODUÇÃO/DEMO)
// ✅ Compatível 100% com DataContext
// ✅ Tratamento de erros profissional
// ✅ Logging estruturado por ambiente
// ✅ Persistência de sessão inteligente
// ✅ Multi-dispositivo (modo Firebase)
// ✅ Offline-first (modo DEMO)
// ✅ Recuperação de senha (Esqueci minha senha)
// ✅ Login com Google integrado
// 🔥 V9: Google Login ULTRA BLINDADO - Timeout + Fallback + Nunca bloquea
// 🔥 V9: _syncCustomClaims con timeout de 8s (no bloquea si falla)
// 🔥 V9: _getUserProfile con timeout de 5s (usa perfil mínimo si falla)
// 🔥 V9: _refreshTokenAndGetClaims con timeout de 5s (usa null si falla)
// 🔥 V9: El usuario SIEMPRE entra, incluso si Cloud Functions no responde
// 🔥 V8: Custom Claims sync via Cloud Function
// 🔥 V8: Token refresh automático pós-login
// 🔥 V8: Limpeza completa no logout (white-label)
// 🔥 V8: Cloud Functions criam perfil (cliente NÃO escreve em users/companies)
// 🔥 V8: updateUserProfile para atualizar perfil no Firestore
// 🔥 V8: signUp para criar usuário no Firebase Auth
// 🔥 V8: Cuentas DEMO siempre usan localStorage (NO Firebase)
// ======================================================

import { 
  auth, 
  db, 
  shouldUseFirebase, 
  firebaseAvailable,
  getFirebaseStatus,
  envLog 
} from '../lib/firebase/config';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  updateProfile,
  signOut, 
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence,
  sendPasswordResetEmail,
  getIdTokenResult
} from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { authenticateUser as authenticateDemoUser } from './database';
import DataContext from '../lib/data/context/DataContext';

// ======================================================
// 📋 CONFIGURAÇÃO
// ======================================================

const AUTH_CONFIG = {
  defaultRole: 'usuario',
  timeout: 30000,
  maxRetries: 2,
  retryDelay: 1000,
  sessionPersistence: 'local',
  debug: import.meta.env.DEV || false,
  enableCustomClaimsSync: true,
  tokenRefreshDelay: 500,  // ms de espera después de setCustomClaims
  // 🔥 V9: Timeouts específicos para Google Login
  googleClaimsTimeout: 8000,    // 8s para sync de claims
  googleProfileTimeout: 5000,   // 5s para obtener perfil
  googleTokenTimeout: 5000,     // 5s para refresh de token
};

// 🔥 ENTERPRISE: Lista COMPLETA de chaves para limpar no logout
const STORAGE_KEYS_TO_CLEAN = [
  'auth_user',
  'auth_token',
  'auth_provider',
  'current_company',
  'company_data',
  'company_settings',
  'white_label_config',
  'userEmail',
  'user_preferences',
  'user_settings',
  'data_strategy',
  'password_reset_requests',
  'email_verify_tokens',
  'demo_session',
  'last_session',
  'firebase_sync_queue',
  'sync_queue_backup',
  'company',
  'user',
  'tenant',
  'whiteLabel'
];

// 🔥 ENTERPRISE: Cuentas que SIEMPRE usan modo DEMO
const DEMO_ACCOUNTS = [
  'admin@imoveispro.com',
  'corretor@imoveispro.com',
  'ana@imoveispro.com',
  'demo@imoveispro.com'
];

// ======================================================
// 🛡️ CLASSE PRINCIPAL - AUTH SERVICE
// ======================================================

class AuthService {
  constructor() {
    this.mode = shouldUseFirebase() ? 'production' : 'demo';
    this.initialized = false;
    this.currentUser = null;
    this.functions = null;
    this._authListeners = [];
    
    if (firebaseAvailable) {
      this._setupPersistence();
      this._setupFunctions();
    }
    
    envLog('AuthService inicializado', { 
      mode: this.mode, 
      firebaseAvailable,
      environment: getFirebaseStatus().environment,
      customClaimsSync: AUTH_CONFIG.enableCustomClaimsSync
    });
  }

  // ======================================================
  // 🔑 CONFIGURAÇÃO DE PERSISTÊNCIA
  // ======================================================
  
  async _setupPersistence() {
    try {
      await setPersistence(auth, browserLocalPersistence);
      envLog('Persistência de sessão configurada', { type: 'local' });
    } catch (error) {
      console.warn('⚠️ Não foi possível configurar persistência:', error.message);
    }
  }

  // 🔥 Configurar Cloud Functions para Custom Claims
  _setupFunctions() {
    try {
      if (auth?.app) {
        this.functions = getFunctions(auth.app, 'us-central1');
        envLog('✅ Cloud Functions inicializadas');
      }
    } catch (error) {
      console.warn('⚠️ Não foi possível inicializar Cloud Functions:', error.message);
    }
  }

  // ======================================================
  // 🔥 SINCRONIZAR CUSTOM CLAIMS (VIA CLOUD FUNCTION)
  // ======================================================
  
  async _syncCustomClaims(userEmail, userName, userPhoto) {
    if (!AUTH_CONFIG.enableCustomClaimsSync) {
      return { success: false, skipped: true };
    }
    
    if (!this.functions || !firebaseAvailable || !auth?.currentUser) {
      return { success: false, unavailable: true };
    }
    
    try {
      envLog('🔄 Sincronizando Custom Claims...');
      
      const onGoogleSignInCF = httpsCallable(this.functions, 'onGoogleSignIn');
      
      const result = await onGoogleSignInCF({
        email: userEmail || auth.currentUser.email,
        displayName: userName || auth.currentUser.displayName,
        photoURL: userPhoto || auth.currentUser.photoURL
      });
      
      if (result?.data?.success) {
        envLog('✅ Custom Claims establecidos por Cloud Function');
        
        // 🔥 CRÍTICO: Esperar y forzar refresh del token
        await new Promise(resolve => setTimeout(resolve, AUTH_CONFIG.tokenRefreshDelay));
        await auth.currentUser.getIdToken(true);
        
        // Verificar que los claims se aplicaron
        const tokenResult = await auth.currentUser.getIdTokenResult();
        
        if (AUTH_CONFIG.debug) {
          console.log('🔐 Claims en token:', tokenResult.claims);
        }
        
        if (!tokenResult.claims.companyId) {
          console.warn('⚠️ Claims incompletos - companyId no encontrado en token');
        }
        
        return { 
          success: true, 
          claims: tokenResult.claims,
          data: result.data 
        };
      }
      
      return { success: false, data: result?.data };
      
    } catch (error) {
      console.error('❌ Error sincronizando Custom Claims:', error);
      // No lanzamos error para no bloquear el login
      return { success: false, error: error.message };
    }
  }

  // 🔥 Refrescar token y obtener claims actuales
  async _refreshTokenAndGetClaims() {
    if (!auth?.currentUser) return null;
    
    try {
      await auth.currentUser.getIdToken(true);
      const tokenResult = await auth.currentUser.getIdTokenResult();
      return tokenResult.claims;
    } catch (error) {
      console.warn('⚠️ Error refrescando token:', error.message);
      return null;
    }
  }

  // ======================================================
  // 🔐 LOGIN UNIFICADO
  // ======================================================
  
  async login(email, password) {
    const startTime = performance.now();
    
    try {
      if (!email || !password) {
        throw new Error('Email e senha são obrigatórios');
      }
      
      if (!this._isValidEmail(email)) {
        throw new Error('Formato de email inválido');
      }
      
      const isDemoAccount = DEMO_ACCOUNTS.includes(email.toLowerCase());
      const useFirebase = !isDemoAccount && shouldUseFirebase();
      
      envLog('Tentativa de login', { 
        email: this._maskEmail(email), 
        mode: useFirebase ? 'firebase' : 'demo'
      });
      
      let user;
      
      if (useFirebase) {
        user = await this._firebaseLogin(email, password);
      } else {
        user = await this._demoLogin(email, password);
      }
      
      this.currentUser = user;
      
      const duration = Math.round(performance.now() - startTime);
      envLog('Login bem-sucedido', { 
        userId: user.uid, 
        mode: user._authProvider, 
        duration: `${duration}ms` 
      });
      
      return user;
      
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      envLog('Login falhou', { 
        email: this._maskEmail(email), 
        error: error.message, 
        duration: `${duration}ms` 
      });
      
      throw this._normalizeError(error);
    }
  }

  // ======================================================
  // 🔥 LOGIN COM FIREBASE AUTH
  // ======================================================
  
  async _firebaseLogin(email, password) {
    const result = await this._withTimeout(
      signInWithEmailAndPassword(auth, email, password),
      AUTH_CONFIG.timeout
    );
    
    const firebaseUser = result.user;
    
    if (AUTH_CONFIG.debug) {
      console.log(`✅ Firebase Auth: ${firebaseUser.uid}`);
    }
    
    // 🔥 Sincronizar Custom Claims (también para email/password)
    await this._syncCustomClaims(email, firebaseUser.displayName, firebaseUser.photoURL);
    
    // Refrescar token para obtener claims actualizados
    const claims = await this._refreshTokenAndGetClaims();
    
    // Obtener perfil de Firestore (creado por Cloud Function)
    const userData = await this._getUserProfile(firebaseUser);
    
    // Construir objeto user con claims del token + datos de Firestore
    const user = this._buildUserObject(firebaseUser, userData, 'firebase', claims);
    
    this._registerInDataContext(user);
    this._saveToLocalStorage(user);
    this._notifyListeners(user);
    
    return user;
  }

  // ======================================================
  // 🎮 LOGIN DEMO (LOCALSTORAGE)
  // ======================================================
  
  async _demoLogin(email, password) {
    const demoUser = await authenticateDemoUser(email, password);
    
    if (!demoUser) {
      throw new Error('Email ou senha incorretos');
    }
    
    const user = this._buildDemoUserObject(demoUser);
    
    this._registerInDataContext(user);
    this._saveToLocalStorage(user);
    this._notifyListeners(user);
    
    return user;
  }

  // ======================================================
  // 🔥 CADASTRO DE USUÁRIO NO FIREBASE AUTH
  // ======================================================
  
  async signUp(email, password, userData = {}) {
    const startTime = performance.now();
    
    try {
      if (!email || !password) {
        throw new Error('Email e senha são obrigatórios');
      }
      
      if (!this._isValidEmail(email)) {
        throw new Error('Formato de email inválido');
      }
      
      if (password.length < 6) {
        throw new Error('A senha deve ter pelo menos 6 caracteres');
      }
      
      if (DEMO_ACCOUNTS.includes(email.toLowerCase())) {
        throw new Error('Email reservado para contas DEMO');
      }
      
      if (!shouldUseFirebase()) {
        throw new Error('Firebase não disponível');
      }
      
      envLog('Criando usuário no Firebase Auth', { 
        email: this._maskEmail(email) 
      });
      
      // 1. Criar usuário no Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // 2. Atualizar displayName
      if (userData.name) {
        try {
          await updateProfile(firebaseUser, { displayName: userData.name });
        } catch (profileError) {
          console.warn('⚠️ Erro atualizando displayName:', profileError.message);
        }
      }
      
      // 3. Disparar Cloud Function onUserCreate (automático)
      // La Cloud Function creará el perfil en Firestore y los Custom Claims
      
      // 4. Esperar y sincronizar claims
      await new Promise(resolve => setTimeout(resolve, 2000));
      await this._syncCustomClaims(email, userData.name);
      
      const duration = Math.round(performance.now() - startTime);
      envLog('Usuário criado', { 
        uid: firebaseUser.uid,
        duration: `${duration}ms`
      });
      
      return firebaseUser;
      
    } catch (error) {
      console.error('❌ Erro no cadastro:', error);
      throw this._normalizeError(error);
    }
  }

  // ======================================================
  // 🔥 V9: LOGIN COM GOOGLE (ULTRA BLINDADO)
  // NUNCA BLOQUEA - Timeouts en cada paso - Fallbacks en cascada
  // El usuario SIEMPRE entra aunque Cloud Functions no responda
  // ======================================================
  
  async loginWithGoogle(googleUser) {
    const startTime = performance.now();
    
    try {
      if (!googleUser || !googleUser.uid) {
        throw new Error('Usuário do Google inválido');
      }
      
      envLog('Processando usuário Google', { 
        email: this._maskEmail(googleUser.email)
      });
      
      // ════════════════════════════════════════════════════
      // PASO 1: Sync Custom Claims (con timeout, no bloquea)
      // ════════════════════════════════════════════════════
      let claimsResult = { success: false, skipped: true };
      
      try {
        const claimsPromise = this._syncCustomClaims(
          googleUser.email,
          googleUser.displayName,
          googleUser.photoURL
        );
        
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout sync claims')), AUTH_CONFIG.googleClaimsTimeout)
        );
        
        claimsResult = await Promise.race([claimsPromise, timeoutPromise]);
        envLog('✅ Claims sync completado', claimsResult);
      } catch (syncError) {
        console.warn('⚠️ [Google Login] Sync claims falló (no bloquea):', syncError.message);
        claimsResult = { success: false, error: syncError.message };
      }
      
      // ════════════════════════════════════════════════════
      // PASO 2: Refresh token y obtener claims (con timeout)
      // ════════════════════════════════════════════════════
      let claims = null;
      
      try {
        const tokenPromise = this._refreshTokenAndGetClaims();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout refresh token')), AUTH_CONFIG.googleTokenTimeout)
        );
        
        claims = await Promise.race([tokenPromise, timeoutPromise]);
      } catch (tokenError) {
        console.warn('⚠️ [Google Login] Refresh token falló (no bloquea):', tokenError.message);
        claims = null;
      }
      
      // ════════════════════════════════════════════════════
      // PASO 3: Obtener perfil de Firestore (con timeout)
      // ════════════════════════════════════════════════════
      let userData = null;
      
      try {
        const profilePromise = this._getUserProfile(googleUser);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout perfil')), AUTH_CONFIG.googleProfileTimeout)
        );
        
        userData = await Promise.race([profilePromise, timeoutPromise]);
      } catch (profileError) {
        console.warn('⚠️ [Google Login] Perfil no cargado (no bloquea):', profileError.message);
        userData = null;
      }
      
      // Si no se obtuvo perfil, crear uno mínimo
      if (!userData) {
        userData = this._createMinimalProfile(googleUser);
      }
      
      // ════════════════════════════════════════════════════
      // PASO 4: Construir objeto user (SIEMPRE funciona)
      // ════════════════════════════════════════════════════
      const user = this._buildUserObject(googleUser, userData, 'google', claims);
      
      // ════════════════════════════════════════════════════
      // PASO 5: Registrar y notificar (operaciones locales)
      // ════════════════════════════════════════════════════
      this.currentUser = user;
      
      this._registerInDataContext(user);
      this._saveToLocalStorage(user);
      this._notifyListeners(user);
      
      const duration = Math.round(performance.now() - startTime);
      envLog('✅ Google login bem-sucedido', { 
        userId: user.uid, 
        duration: `${duration}ms`,
        claimsSynced: !!claims?.companyId,
        profileFromFirestore: !!userData?.uid
      });
      
      return user;
      
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      
      // 🔥 V9: Si todo falla, intentar construir un usuario mínimo
      if (googleUser?.uid && googleUser?.email) {
        console.warn('⚠️ [Google Login] Error crítico, construyendo usuario mínimo:', error.message);
        try {
          const minimalUser = this._buildUserObject(
            googleUser,
            this._createMinimalProfile(googleUser),
            'google',
            null
          );
          this.currentUser = minimalUser;
          this._saveToLocalStorage(minimalUser);
          this._notifyListeners(minimalUser);
          
          envLog('⚠️ Google login com perfil mínimo', { 
            userId: minimalUser.uid,
            duration: `${duration}ms`,
            fallback: true
          });
          
          return minimalUser;
        } catch (fallbackError) {
          console.error('❌ Fallback también falló:', fallbackError);
        }
      }
      
      envLog('Google login falhou', { 
        error: error.message, 
        duration: `${duration}ms` 
      });
      throw this._normalizeError(error);
    }
  }

  // ======================================================
  // 🔥 ATUALIZAR PERFIL NO FIRESTORE
  // ======================================================
  
  async updateUserProfile(uid, updates) {
    try {
      if (!uid || !updates || typeof updates !== 'object') {
        throw new Error('UID e dados de atualização são obrigatórios');
      }
      
      if (!shouldUseFirebase() || !db) {
        return { success: true, localOnly: true };
      }
      
      const userRef = doc(db, 'users', uid);
      
      const allowedFields = ['name', 'avatar', 'phone', 'color', 'preferences', 'settings'];
      const filteredUpdates = {};
      
      Object.keys(updates).forEach(key => {
        if (allowedFields.includes(key) && updates[key] !== undefined) {
          filteredUpdates[key] = updates[key];
        }
      });
      
      if (Object.keys(filteredUpdates).length === 0) {
        return { success: true, noChanges: true };
      }
      
      await updateDoc(userRef, {
        ...filteredUpdates,
        updatedAt: new Date().toISOString()
      });
      
      // El trigger onUserProfileUpdated actualizará los claims automáticamente
      
      envLog('Perfil atualizado no Firestore', { 
        uid, 
        fields: Object.keys(filteredUpdates) 
      });
      
      return { success: true, updatedFields: Object.keys(filteredUpdates) };
      
    } catch (error) {
      console.error('❌ Erro atualizando perfil:', error);
      throw error;
    }
  }

  // ======================================================
  // 🔑 RECUPERAÇÃO DE SENHA
  // ======================================================
  
  async forgotPassword(email) {
    try {
      if (!email || !this._isValidEmail(email)) {
        throw new Error('Email inválido');
      }
      
      const useFirebase = shouldUseFirebase();
      
      if (useFirebase) {
        try {
          await sendPasswordResetEmail(auth, email, {
            url: `${window.location.origin}/login`,
            handleCodeInApp: true
          });
          
          return { 
            success: true, 
            message: 'Email de recuperação enviado',
            provider: 'firebase'
          };
        } catch (error) {
          if (error.code === 'auth/user-not-found') {
            return { 
              success: true, 
              message: 'Se o email existir, você receberá instruções'
            };
          }
          throw error;
        }
      } else {
        const { forgotPassword: demoForgotPassword } = await import('./database');
        const demoResult = await demoForgotPassword(email);
        
        return {
          success: demoResult.success,
          message: demoResult.success ? 'Email de recuperação enviado' : 'Erro ao processar',
          token: demoResult.token,
          provider: 'demo'
        };
      }
    } catch (error) {
      return {
        success: false,
        message: error.message
      };
    }
  }

  async resetPassword(token, newPassword) {
    try {
      if (!token || !newPassword || newPassword.length < 6) {
        throw new Error('Dados inválidos');
      }
      
      const useFirebase = shouldUseFirebase();
      
      if (useFirebase) {
        // Firebase no tiene resetPassword directo por token
        return { success: true, message: 'Use o link do email para redefinir', provider: 'firebase' };
      } else {
        const { resetPassword: demoResetPassword } = await import('./database');
        const result = await demoResetPassword(token, newPassword);
        
        return {
          success: result,
          message: result ? 'Senha atualizada' : 'Erro ao atualizar',
          provider: 'demo'
        };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  }

  // ======================================================
  // 👤 OBTER PERFIL DO FIRESTORE
  // ======================================================
  
  async _getUserProfile(firebaseUser) {
    if (!db) return this._createMinimalProfile(firebaseUser);
    
    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return userDoc.data();
      }
      
      console.warn('⚠️ Perfil não encontrado no Firestore, usando dados mínimos');
      return this._createMinimalProfile(firebaseUser);
      
    } catch (error) {
      console.error('❌ Erro obtendo perfil:', error);
      return this._createMinimalProfile(firebaseUser);
    }
  }

  _createMinimalProfile(firebaseUser) {
    const companyId = `company_${firebaseUser.uid}`;
    
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Usuário',
      role: AUTH_CONFIG.defaultRole,
      companyId,
      company: companyId,
      avatar: firebaseUser.photoURL || null,
      phone: firebaseUser.phoneNumber || '',
      emailVerified: firebaseUser.emailVerified || false,
      permissions: {
        create: true,
        read: true,
        update: true,
        delete: false,
        manageUsers: false,
        manageCompany: false
      }
    };
  }

  // ======================================================
  // 🏗️ CONSTRUTORES DE OBJETO USER
  // ======================================================
  
  _buildUserObject(firebaseUser, userData, provider, claims = null) {
    // Priorizar datos de claims (vienen del token, son los más actualizados)
    const companyId = claims?.companyId || userData?.companyId || `company_${firebaseUser.uid}`;
    const role = claims?.role || userData?.role || AUTH_CONFIG.defaultRole;
    
    return {
      uid: firebaseUser.uid,
      id: firebaseUser.uid,
      email: firebaseUser.email,
      emailVerified: firebaseUser.emailVerified || false,
      name: userData?.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0],
      role,
      avatar: userData?.avatar || firebaseUser.photoURL,
      phone: userData?.phone || firebaseUser.phoneNumber || '',
      companyId,
      company: companyId,
      color: userData?.color || '#2563eb',
      
      // Permisos: priorizar claims del token, luego Firestore, luego defaults
      canWrite: claims?.canWrite !== undefined ? claims.canWrite : (userData?.permissions?.create !== false),
      canDelete: claims?.canDelete === true || role === 'admin',
      canRead: claims?.canRead !== undefined ? claims.canRead : (userData?.permissions?.read !== false),
      canManageUsers: claims?.canManageUsers === true || role === 'admin',
      canManageCompany: claims?.canManageCompany === true || role === 'admin',
      
      createdAt: userData?.createdAt || new Date().toISOString(),
      updatedAt: userData?.updatedAt || new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      
      _isAuthenticated: true,
      _securityLevel: role,
      _tenantId: companyId,
      _authProvider: provider,
      _permissions: userData?.permissions || this._getDefaultPermissions(role)
    };
  }

  _buildDemoUserObject(demoUser) {
    const companyId = demoUser.company || `company_${demoUser.id}`;
    
    return {
      uid: demoUser.id,
      id: demoUser.id,
      email: demoUser.email,
      emailVerified: demoUser.emailVerified || false,
      name: demoUser.name,
      role: demoUser.role || AUTH_CONFIG.defaultRole,
      avatar: demoUser.avatar,
      phone: demoUser.phone || '',
      companyId,
      company: companyId,
      color: '#2563eb',
      canWrite: true,
      canDelete: demoUser.role === 'admin',
      canRead: true,
      canManageUsers: demoUser.role === 'admin',
      canManageCompany: demoUser.role === 'admin' || demoUser.role === 'corretor',
      createdAt: demoUser.createdAt || new Date().toISOString(),
      updatedAt: demoUser.updatedAt || new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      _isAuthenticated: true,
      _securityLevel: demoUser.role || 'user',
      _tenantId: companyId,
      _authProvider: 'demo',
      _permissions: this._getDefaultPermissions(demoUser.role)
    };
  }

  _getDefaultPermissions(role) {
    const isAdmin = role === 'admin';
    const isCorretor = role === 'corretor';
    
    return {
      create: true,
      read: true,
      update: true,
      delete: isAdmin,
      manageUsers: isAdmin,
      manageCompany: isAdmin || isCorretor,
      exportData: isAdmin,
      viewReports: true,
      manageProperties: true,
      manageClients: true
    };
  }

  // ======================================================
  // 📡 LISTENERS DE AUTENTICAÇÃO
  // ======================================================
  
  onAuthChange(callback) {
    this._authListeners.push(callback);
    
    if (shouldUseFirebase() && auth) {
      return onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          try {
            const userData = await this._getUserProfile(firebaseUser);
            const claims = await this._refreshTokenAndGetClaims();
            const user = this._buildUserObject(firebaseUser, userData, 'firebase', claims);
            this.currentUser = user;
            this._saveToLocalStorage(user);
            callback(user);
          } catch (error) {
            console.error('❌ Erro no listener de auth:', error);
            callback(null);
          }
        } else {
          this.currentUser = null;
          callback(null);
        }
      });
    }
    
    // Modo DEMO: verificar localStorage
    const savedUser = localStorage.getItem('auth_user');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        this.currentUser = user;
        callback(user);
      } catch (error) {
        callback(null);
      }
    } else {
      callback(null);
    }
    
    return () => {
      this._authListeners = this._authListeners.filter(l => l !== callback);
    };
  }

  _notifyListeners(user) {
    this._authListeners.forEach(listener => {
      try { listener(user); } catch (e) {}
    });
  }

  // ======================================================
  // 🔓 LOGOUT
  // ======================================================
  
  async logout() {
    const userId = this.currentUser?.uid;
    
    try {
      envLog('Encerrando sessão', { userId });
      
      // 1. Firebase signOut
      if (shouldUseFirebase() && auth) {
        try { 
          await signOut(auth); 
          envLog('Firebase signOut concluído'); 
        } catch (e) { 
          console.warn('⚠️ Erro no Firebase signOut:', e.message); 
        }
      }
      
      // 2. Limpiar DataContext
      if (userId) {
        try { 
          DataContext.clearInstance(userId); 
          envLog('DataContext limpo'); 
        } catch (e) { 
          console.warn('⚠️ Erro limpando DataContext:', e.message); 
        }
      }
      
      // 3. Limpiar storage
      this._cleanAllStorage();
      
      // 4. Limpiar sessionStorage
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }
      
      // 5. Resetear estado
      this.currentUser = null;
      this._notifyListeners(null);
      
      envLog('✅ Sessão encerrada com sucesso');
      
    } catch (error) {
      console.error('❌ Erro no logout:', error);
      this._forceCleanup();
      throw error;
    }
  }

  _cleanAllStorage() {
    if (typeof localStorage === 'undefined') return;
    
    const cleanedKeys = [];
    
    // Limpiar keys específicas
    STORAGE_KEYS_TO_CLEAN.forEach(key => {
      try {
        if (localStorage.getItem(key) !== null) {
          localStorage.removeItem(key);
          cleanedKeys.push(key);
        }
      } catch (e) {}
    });
    
    // Limpiar keys dinámicas
    const dynamicPrefixes = ['company_', 'user_', 'doc_version_', 'sync_', 'demo_', 'temp_', 'imoveispro_db_'];
    try {
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        const shouldClean = dynamicPrefixes.some(prefix => key.startsWith(prefix));
        if (shouldClean && !STORAGE_KEYS_TO_CLEAN.includes(key)) {
          localStorage.removeItem(key);
          cleanedKeys.push(key);
        }
      });
    } catch (e) {}
    
    envLog('Storage limpo', { cleanedCount: cleanedKeys.length });
  }

  _forceCleanup() {
    const essentialKeys = ['auth_user', 'auth_token', 'auth_provider', 'current_company'];
    essentialKeys.forEach(key => {
      try { localStorage.removeItem(key); } catch (e) {}
    });
    this.currentUser = null;
    this._notifyListeners(null);
    console.log('🧹 Limpeza forçada concluída');
  }

  // ======================================================
  // 📊 ESTADO E UTILITÁRIOS
  // ======================================================
  
  _registerInDataContext(user) {
    try {
      DataContext.setCurrentUser(user.uid, user.email, user.companyId);
    } catch (error) {
      console.warn('⚠️ Não foi possível registrar no DataContext:', error.message);
    }
  }

  _saveToLocalStorage(user) {
    try {
      const userToStore = { ...user };
      delete userToStore._permissions;  // No guardar en localStorage
      localStorage.setItem('auth_user', JSON.stringify(userToStore));
      localStorage.setItem('auth_token', this._generateSessionToken(user));
      localStorage.setItem('auth_provider', user._authProvider);
    } catch (error) {
      console.warn('⚠️ Não foi possível salvar no localStorage:', error.message);
    }
  }

  _generateSessionToken(user) {
    const tokenData = {
      userId: user.uid,
      email: user.email,
      role: user.role,
      provider: user._authProvider,
      exp: Date.now() + 7 * 24 * 60 * 60 * 1000
    };
    return btoa(JSON.stringify(tokenData));
  }

  getCurrentUser() { return this.currentUser; }
  isAuthenticated() { return !!this.currentUser?._isAuthenticated; }
  getMode() { return this.mode; }
  
  getAuthStatus() {
    return {
      isAuthenticated: this.isAuthenticated(),
      mode: this.mode,
      user: this.currentUser ? {
        uid: this.currentUser.uid,
        email: this.currentUser.email,
        role: this.currentUser.role,
        provider: this.currentUser._authProvider
      } : null,
      firebaseStatus: getFirebaseStatus()
    };
  }

  // ======================================================
  // 🛠️ HELPERS
  // ======================================================
  
  async _withTimeout(promise, timeoutMs) {
    const timeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Timeout de operação')), timeoutMs)
    );
    return Promise.race([promise, timeout]);
  }

  _isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
  
  _maskEmail(email) {
    if (!email) return 'unknown';
    const [name, domain] = email.split('@');
    if (!domain) return email;
    return `${name.substring(0, 3)}***@${domain}`;
  }

  _normalizeError(error) {
    const errorMessages = {
      'auth/user-not-found': 'Usuário não encontrado',
      'auth/wrong-password': 'Senha incorreta',
      'auth/invalid-email': 'Email inválido',
      'auth/user-disabled': 'Usuário desabilitado',
      'auth/too-many-requests': 'Muitas tentativas. Tente mais tarde',
      'auth/network-request-failed': 'Erro de conexão',
      'auth/invalid-credential': 'Credenciais inválidas',
      'auth/email-already-in-use': 'Este email já está registrado',
      'auth/weak-password': 'A senha é muito fraca',
      'auth/operation-not-allowed': 'Operação não permitida',
      'Timeout de operação': 'Tempo esgotado. Tente novamente'
    };
    
    const message = errorMessages[error.code] || errorMessages[error.message] || error.message || 'Erro desconhecido';
    const normalizedError = new Error(message);
    normalizedError.code = error.code;
    normalizedError.originalError = error;
    return normalizedError;
  }
}

// ======================================================
// 📤 EXPORT
// ======================================================

const authService = new AuthService();

export default authService;
export { authService, AuthService, AUTH_CONFIG };